import assign from 'lodash.assign';

const { createHigherOrderComponent } = wp.compose;
const { Fragment } = wp.element;
const { BlockControls } = wp.blockEditor;
const { Toolbar, IconButton } = wp.components;
const { addFilter } = wp.hooks;
const { __ } = wp.i18n;

// Blocks that are not compatible at all with visibility controls.
const globallyRestricted = ['core/freeform', 'core/legacy-widget', 'core/widget-area'];

/**
 * Add layout control attribute to block.
 *
 * @param {object} settings Current block settings.
 * @param {string} name Name of block.
 *
 * @returns {object} Modified block settings.
 */
const addDisableBlockControlAttribute = (settings, name) => {
	// The freeform (Classic Editor) block is incompatible because it does not
	// support custom attributes.
	if (globallyRestricted.includes(settings.name)) {
		return settings;
	}

	if (typeof settings.attributes !== 'undefined') {
		const attributes = {
			disableBlock: {
				type: 'boolean',
				default: false,
			},
		}

		// Use Lodash's assign to gracefully handle if attributes are undefined
		settings.attributes = assign(settings.attributes, attributes);
		settings.supports = assign(settings.supports, {
			disableBlock: false,
		});
	}

	return settings;
};

addFilter('blocks.registerBlockType', 'disableBlock/attribute', addDisableBlockControlAttribute);

/**
 * Create HOC to add layout control to inspector controls of block.
 */
const withLogicControl = createHigherOrderComponent((BlockEdit) => {
	return (props) => {
		const { attributes, setAttributes, isSelected } = props;
		const { disableBlock } = attributes;

		if (disableBlock) {
			attributes.disableBlock = disableBlock;
		}

		return (
			<Fragment>
				<BlockEdit {...props} />
				{isSelected && (!globallyRestricted.includes(props.name)) &&
					<BlockControls>
						<Toolbar>
							<IconButton
								label={attributes.disableBlock ? __('Enable Block', 'disable-block'): __('Disable Block', 'disable-block')}
								icon={attributes.disableBlock ? 'hidden' : 'visibility'}
								onClick={() => {
									const newValue = !attributes.disableBlock;
									setAttributes({ disableBlock: newValue });

									// Trigger sync after a slight delay to let React update the DOM
									setTimeout(() => {
										addDataAttributeToListView();
									}, 200);
								}}
							/>
						</Toolbar>
					</BlockControls>
				}
			</Fragment>
		);
	};
}, 'withLogicControl');

addFilter('editor.BlockEdit', 'disableBlock/control', withLogicControl);

// add a data attribute to the block wrapper "editor-block-list__block"
// this hook only fires for the BE/Admin View
const addWrapperDataAttribute = createHigherOrderComponent((BlockListBlock) => {
	return props => {
		const {
			disableBlock,
		} = props.attributes;

		const {
			name,
		} = props.block;

		let newName = name.replace(/\w+\//gm, '');
		newName = newName.charAt(0).toUpperCase() + newName.slice(1);

		let wrapperProps = props.wrapperProps;
		let customData = {};

		if (typeof disableBlock !== 'undefined' && disableBlock) {
			customData = assign(customData, {'data-spk-block-disabled': __('Block', 'disable-block') + ' ' + newName + ' ' + __('is disabled', 'disable-block')});
		}

		wrapperProps = assign(wrapperProps, customData);

		return <BlockListBlock {...props} wrapperProps={wrapperProps}/>;
	};
}, 'addWrapperDataAttribute');

addFilter(
	'editor.BlockListBlock',
	'disableBlock/addWrapperDataAttribute',
	addWrapperDataAttribute,
);

const addDataAttributeToListView = () => {
	const { select } = wp.data;
	const allBlocks = select('core/block-editor').getBlocks();

	const seenClientIds = new Set();

	allBlocks.forEach((block) => {
		const blockId = block.clientId;
		const listViewRow = document.querySelector(`.block-editor-list-view-leaf[data-block="${blockId}"]`);
		if (!listViewRow) return;

		seenClientIds.add(blockId);

		if (block.attributes.disableBlock) {
			listViewRow.setAttribute('data-spk-block-disabled', 'true');
		} else {
			listViewRow.removeAttribute('data-spk-block-disabled');
		}
	});

	// Optional: Clean up orphaned attributes if blocks were removed
	const allListRows = document.querySelectorAll('.block-editor-list-view-leaf[data-spk-block-disabled]');
	allListRows.forEach((row) => {
		const blockId = row.getAttribute('data-block');
		if (!seenClientIds.has(blockId)) {
			row.removeAttribute('data-spk-block-disabled');
		}
	});
};

let observer = null;

const setupBodyContainerObserver = () => {
	if (observer) return; // Already observing

	observer = new MutationObserver(() => {
		const leafs = document.querySelectorAll('.block-editor-list-view-leaf');
		if (leafs.length > 0) {
			addDataAttributeToListView();
		}
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});
};

window.addEventListener('load', () => {
	setupBodyContainerObserver();
});
